/** The Auditor's overview: the first tab of Approvals & audit for an Auditor, and read-only throughout.
 *
 * An Auditor's work is the evidence behind a filing, so this is where they start (ADR-0212 decision
 * 9). It was a block inside Action items, laid into a one-column grid track of the page and drawn one
 * word per line (U7); it is a table page now, on the standard frame: a header card, four tiles and
 * **one** table of what the audit covers (the reporting periods and the filings), with the
 * explanations behind info controls.
 *
 * **Read-only is drawn, not claimed.** The page holds no form and no control that writes: a count, a
 * state, a link to the screen that holds the evidence. An Auditor's one act (raising an evidence
 * request) lives on the Assurance tab, where the server gates it.
 *
 * **The first rows are the terms** (Q-03, G-Wauditor-06): which companies, which period and until
 * when. An Auditor could not read the terms of their own engagement - the page said *Period* in a
 * chip and listed the audit's periods, but not the companies, the window or the day access ends. The
 * window and the end are the person's own membership (`/auth/me`'s `engagement_terms`); the companies
 * are what `GET /entities` returns, which the database already clamps to the engagement, so there is
 * no second list of them to disagree with the policies.
 *
 * Four honest answers, each from the read that owns it, and a read that fails says so and never
 * becomes a zero:
 *
 * - **Chain check** - the ledger's own check or, for an engagement, the words *not shown* (an entry has
 *   no period or company to narrow it to; ADR-0048). Not a failed read, and not "fine".
 * - **Requests open** - the assurance queue, counted by the states that are still live.
 * - **Periods closed** - the workspace's own list of reporting periods.
 * - **Filings issued** - the filings with a version issued, of those owed under law in force (an enjoined,
 *   undated regime is listed and not counted).
 *
 * **The ledger and Activity tabs are not offered to an Auditor** (they cannot yet be narrowed to an
 * engagement; ADR-0213, proposed), so an Auditor who is not engaged and may read the whole chain
 * finds it here, and an engaged one is pointed at each record's History.
 */

import { Eye } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { isLive, useAssuranceQueue } from "../api/assurance";
import { useMe } from "../api/auth";
import { useHomeSummary } from "../api/homeSummary";
import { useChain } from "../api/ledger";
import { useReader } from "../api/profile";
import { useEntities } from "../api/queries";
import { ReadFailed } from "../components/Loaded";
import { EmptyState, GridTable, InfoPopover, StatusChip, TablePage, Tile, TileStrip } from "../components/ui";
import type { GridColumn } from "../components/ui";
import { instantDate, shortDate } from "../lib/format";
import { RequireSession } from "../shell/RequireSession";
import { isIssuedState, stateWords } from "./filingState";
import { filingTitle, isLive as isOperative } from "./homeWords";
import { CHECK } from "./ledgerWords";
import { keepSearch } from "./search";
import type { ScreenSearch } from "./search";
import { ScreenFrame } from "./ScreenFrame";
import shared from "./Screen.module.css";
import { useScreen } from "./useScreen";
import type { Screen } from "./useScreen";

interface Row {
  id: string;
  kind: "Engagement" | "Period" | "Filing";
  name: ReactNode;
  covers: string;
  state: ReactNode;
}

/** Its own component so the reader's preferences are read only when a closed period has a date. */
function ClosedOn({ at }: { at: string | null | undefined }) {
  const reader = useReader();
  return <>Closed {instantDate(at ?? null, reader)}</>;
}

const COLUMNS: GridColumn<Row>[] = [
  { id: "kind", header: "Item", cell: (row) => row.kind },
  { id: "name", header: "Name", rowHeader: true, cell: (row) => row.name },
  { id: "covers", header: "Covers", cell: (row) => row.covers },
  { id: "state", header: "State", cell: (row) => row.state },
];

function Overview({ screen }: { screen: Screen }) {
  const { data: me } = useMe();
  const engaged = me?.engagement ?? null;
  const method = screen.method ?? "location";
  // The chain is not asked for when the server would refuse it by design, exactly as the ledger
  // screen does: a request made only to be answered 403 is a failed call in every log.
  const chain = useChain(undefined, me !== undefined && engaged === null);
  const queue = useAssuranceQueue(true);
  const companies = useEntities();
  const summary = useHomeSummary(screen.window, method);
  const periods = screen.organization?.periods ?? [];
  const closed = periods.filter((period) => period.status === "locked");

  const live = queue.data ? queue.data.filter(isLive).length : null;
  const filings = summary.data?.filings ?? null;
  // **One definition of each** (G-Wauditor-03): a filing is issued when any version of it is, whatever
  // the checks say now, and it is owed when it is operative law - SB 261, enjoined and undated, is listed
  // below and is not counted as outstanding to an auditor who may report it.
  const issued = filings ? filings.filter((row) => isIssuedState(row.state)).length : null;
  const owed = filings ? filings.filter(isOperative) : null;
  const chainState = chain.data ? CHECK[chain.data.check.state] : undefined;

  const terms = me?.engagement_terms ?? null;
  const first = terms?.period_start ?? null;
  const last = terms?.period_end ?? null;
  const endsOn = terms?.access_ends_on ?? null;
  // **The terms, as rows of the one table** (the page is a title, tiles and one table): a label, the
  // value, and a state only where one is true. Read from the person's own membership and from the
  // companies the database lets them read, and a company list that failed says so.
  const termRows: Row[] = [
    {
      id: "terms-companies",
      kind: "Engagement",
      name: "Companies",
      covers: companies.data
        ? companies.data.map((company) => company.legal_name).join(", ") || "None"
        : companies.error
          ? "Not read"
          : "Reading",
      state: "—",
    },
    {
      id: "terms-period",
      kind: "Engagement",
      name: "Period you may read",
      covers:
        first && last
          ? `${shortDate(first)} to ${shortDate(last)}`
          : first
            ? `From ${shortDate(first)}`
            : last
              ? `Up to ${shortDate(last)}`
              : "Every period",
      state: "—",
    },
    {
      id: "terms-access",
      kind: "Engagement",
      name: "Your access ends",
      covers: endsOn ? shortDate(endsOn) : "No end date",
      state: (
        <StatusChip tone="neutral" size="sm">
          {endsOn ? "Ends" : "Open"}
        </StatusChip>
      ),
    },
  ];

  const rows: Row[] = [
    ...(me ? termRows : []),
    ...periods.map(
      (period): Row => ({
        id: `period-${period.id}`,
        kind: "Period",
        name: `${period.regime_key.replace(/_/g, " ")}, ${period.label}`,
        covers: `${shortDate(period.start_date)} to ${shortDate(period.end_date)}`,
        state: (
          <StatusChip tone={period.status === "locked" ? "ok" : "neutral"} size="sm">
            {period.status === "locked" ? <ClosedOn at={period.closed_at} /> : "Open"}
          </StatusChip>
        ),
      }),
    ),
    ...(filings ?? []).map(
      (row): Row => ({
        id: `filing-${row.obligation_id}`,
        kind: "Filing",
        name: (
          <Link
            to="/reports"
            search={(prev: Partial<ScreenSearch>) => ({
              ...keepSearch(prev),
              filing: row.obligation_id,
              tab: isIssuedState(row.state) ? "issue" : undefined,
            })}
            className={shared.fixLink}
          >
            {filingTitle(row)}, {row.legal_entity_name}
          </Link>
        ),
        covers: `Year ended ${shortDate(row.financial_year_end)}`,
        state: (
          <StatusChip tone={isIssuedState(row.state) ? stateWords(row.state).tone : "neutral"} size="sm">
            {isIssuedState(row.state) ? stateWords(row.state).word : "Not issued"}
          </StatusChip>
        ),
      }),
    ),
  ];

  const scope =
    engaged === "period" ? "Period" : engaged === "companies" ? "Companies" : "Whole workspace";

  return (
    <TablePage
      icon={Eye}
      title="Approvals and audit"
      label="Engagement"
      actions={
        <>
          {/* "Read-only" is the top bar's chip, on every page an Auditor opens. */}
          <StatusChip tone="neutral" testId="auditor-scope">
            {scope}
          </StatusChip>
          {/* An Auditor who is not engaged may read the whole chain; the tab is not offered, so the
              way in is here. An engaged one is refused by the server and is not shown the link. */}
          {engaged === null ? (
            <Link to="/ledger" search={keepSearch} className={shared.fixLink}>
              Read the audit ledger
            </Link>
          ) : null}
          <InfoPopover infoKey="audit.engagement" />
        </>
      }
      tiles={
        <TileStrip label="The audit at a glance">
          <Tile
            label="Chain check"
            testId="audit-chain"
            value={
              engaged ? "Not shown" : chainState ? chainState.short : chain.error ? "Not read" : "Reading"
            }
            unit={
              !engaged && chain.data
                ? `${String(chain.data.entries)} entries, ${String(chain.data.check.entries_checked)} recomputed`
                : undefined
            }
            infoKey="audit.engagement-chain"
          />
          <Tile
            label="Requests open"
            testId="audit-requests"
            value={live === null ? (queue.error ? "Not read" : "Reading") : String(live)}
            unit={queue.data ? `of ${String(queue.data.length)} raised` : undefined}
          />
          <Tile
            label="Periods closed"
            testId="audit-closed"
            value={`${String(closed.length)} of ${String(periods.length)}`}
          />
          <Tile
            label="Filings issued"
            testId="audit-issued"
            value={issued === null ? (summary.error ? "Not read" : "Reading") : String(issued)}
            unit={owed ? `of ${String(owed.length)} owed` : undefined}
          />
        </TileStrip>
      }
      extra={
        <>
          {chain.error && engaged === null ? (
            <ReadFailed of="The chain" error={chain.error} onRetry={() => void chain.refetch()} />
          ) : null}
          {companies.error ? (
            <ReadFailed
              of="The companies you are engaged for"
              error={companies.error}
              onRetry={() => void companies.refetch()}
            />
          ) : null}
          {queue.error ? (
            <ReadFailed
              of="The evidence requests"
              error={queue.error}
              onRetry={() => void queue.refetch()}
            />
          ) : null}
        </>
      }
      table={
        summary.error ? undefined : (
          <GridTable
            caption="What the audit covers"
            columns={COLUMNS}
            rows={rows}
            rowKey={(row) => row.id}
            loading={summary.isPending && screen.window !== undefined}
            testId="engagement-table"
            empty={<EmptyState compact title="No reporting period or filing yet" />}
          />
        )
      }
      state={
        summary.error ? (
          <ReadFailed of="The filings" error={summary.error} onRetry={() => void summary.refetch()} />
        ) : undefined
      }
    />
  );
}

function EngagementBody() {
  const screen = useScreen();
  const { data: me } = useMe();
  return (
    <ScreenFrame
      screen={screen}
      title="Engagement"
      crumb="what the audit covers, and nothing on it changes anything"
      bodyScrolls
    >
      {me?.role === "auditor" ? (
        <Overview screen={screen} />
      ) : (
        <TablePage
          icon={Eye}
          title="Approvals and audit"
          label="Engagement"
          state={<p data-testid="engagement-for-auditors">This overview is for an Auditor.</p>}
        />
      )}
    </ScreenFrame>
  );
}

export function EngagementScreen() {
  return (
    <RequireSession>
      <EngagementBody />
    </RequireSession>
  );
}
